import React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useDispatch } from 'react-redux';

import {
  LeadAnalyticsData,
  sendLegacyProjectContact,
  sendLegacyProjectShowPhoneContact,
  sendLegacyProjectWhatsappContact,
  setStatus
} from '../redux/slices/contactForm';

export type UseLegacyProjectContactFormSendMutationArgs = LeadAnalyticsData & {
  projectCommuneId: number;
  projectId: number;
};

type SendContactFn = (
  args: UseLegacyProjectContactFormSendMutationArgs
) => void;

export type UseLegacyProjectContactFormSendMutation = {
  sendContact: SendContactFn;
  sendWhatsappContact: SendContactFn;
  sendShowPhoneContact: SendContactFn;
};

export default function useLegacyProjectContactForm(): UseLegacyProjectContactFormSendMutation {
  const dispatch = useDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const sendForm = React.useCallback(
    async (
      action:
        | typeof sendLegacyProjectContact
        | typeof sendLegacyProjectWhatsappContact
        | typeof sendLegacyProjectShowPhoneContact,
      args: UseLegacyProjectContactFormSendMutationArgs
    ) => {
      if (!executeRecaptcha) {
        dispatch(setStatus('error'));

        return;
      }

      try {
        const recaptchaAction = 'contactForm';
        const token = await executeRecaptcha(recaptchaAction);

        dispatch(
          action({
            category: args.category,
            city: args.city,
            currency: args.currency,
            operation: args.operation,
            province: args.province,
            region: args.region,
            value: args.value,
            legacyProjectCommuneId: args.projectCommuneId,
            legacyProjectId: args.projectId,
            recaptchaToken: token,
            recaptchaAction
          })
        );
      } catch (error) {
        dispatch(setStatus('error'));
      }
    },
    [dispatch, executeRecaptcha]
  );

  const sendContact: SendContactFn = React.useCallback(
    (args: UseLegacyProjectContactFormSendMutationArgs) => {
      sendForm(sendLegacyProjectContact, args);
    },
    [sendForm]
  );

  const sendWhatsappContact = React.useCallback(
    (args: UseLegacyProjectContactFormSendMutationArgs) => {
      sendForm(sendLegacyProjectWhatsappContact, args);
    },
    [sendForm]
  );

  const sendShowPhoneContact = React.useCallback(
    (args: UseLegacyProjectContactFormSendMutationArgs) => {
      sendForm(sendLegacyProjectShowPhoneContact, args);
    },
    [sendForm]
  );

  return { sendContact, sendWhatsappContact, sendShowPhoneContact };
}
